/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, Input, NgZone, OnChanges, SimpleChanges} from '@angular/core';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';
import * as _ from 'lodash';
import {TranslateService} from '@ngx-translate/core';
import {FormsModule} from '@angular/forms';
import {UIRouterModule} from '@uirouter/angular';
import template from './segment-side-panel.component.html';
import {triggerConditionFormatter} from '../trigger/trigger-util';
import {AngularModalService} from '../common/modal/angular-modal.service';
import {SegmentTriggerModalComponent} from './segment-trigger-modal.component';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {TriggerApiService} from '../trigger/trigger-api.service';
import {AppSharedModule} from '../common/app-shared.module';
import {ChartsModule} from '../common/directives/charts.module';
import {StatsTablesModule} from '../common/controller/stats-tables.module';

@Component({
  standalone: true,
  imports: [AppSharedModule, FormsModule, UIRouterModule, ChartsModule, StatsTablesModule],
  selector: 'adn-segment-side-panel',
  template
})
export class SegmentSidePanelComponent implements OnChanges {
  @Input() model: any;
  @Input() isNew: boolean;

  effectiveIsNew = false;
  activeTab: 'triggers' | 'charts' = 'triggers';
  hasAdvertising = false;
  showingCharts = false;
  selFolder: any;

  listReady = false;
  /** Buffered so asynchronous list replacement does not race template checks. */
  displayItems: any[] = [];
  hasItems = false;

  constructor(
    private readonly triggerApi: TriggerApiService,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly translate: TranslateService,
    private readonly angularModalService: AngularModalService,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.hasAdvertising = this.LocalNetworkProfile.isAdvertisingPlatform();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isNew) {
      this.effectiveIsNew = !!this.isNew;
    }
    if (changes.model) {
      this.syncTriggersList();
      this.selFolder = _.get(this.model, ['folderMembers', 0]);
    }
  }

  selectTab(tab: 'triggers' | 'charts'): void {
    this.activeTab = tab;
    if (tab === 'charts') {
      this.showingCharts = true;
    }
  }

  triggerFormatter(entry: any): string[] {
    return triggerConditionFormatter(this.translate, entry);
  }

  doTrigger(entry?: any): void {
    const modalRef = this.angularModalService.show(SegmentTriggerModalComponent, {
      class: 'modal-lg adnDialog',
      initialState: {
        segmentModel: this.model,
        entry
      }
    });

    const modalContent = modalRef.content as SegmentTriggerModalComponent;
    modalContent.onClosed.subscribe(() => {
      this.ngZone.run(() => {
        this.loadTriggers();
      });
    });
    modalRef.onHidden?.subscribe(() => {
      this.ngZone.run(() => {
        this.loadTriggers();
      });
    });
  }

  private syncTriggersList(): void {
    if (this.effectiveIsNew || !this.model?.id) {
      this.displayItems = [];
      this.hasItems = false;
      this.listReady = true;
      return;
    }

    this.displayItems = [];
    this.hasItems = false;
    this.loadTriggers();
  }

  private loadTriggers(): void {
    if (!this.model?.id) {
      return;
    }
    this.listReady = false;
    this.triggerApi.listForSegment(this.model.id).subscribe({
      next: (page) => {
        deferMarkForCheck(this.cdr, () => {
          this.displayItems = (page.results || []).slice();
          this.hasItems = this.displayItems.length > 0;
          this.listReady = true;
        });
      },
      error: () => {
        deferMarkForCheck(this.cdr, () => {
          this.displayItems = [];
          this.hasItems = false;
          this.listReady = true;
        });
      }
    });
  }

}
